import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import StepTrackerService from "../service/StepTrackerService";
import "../styles/stepTrend.scss";

/**
 * Step count trend — modal opened from the "View trend" button on the
 * Physical activity block. One month at a time with prev/next arrows +
 * a native month input for jumping around. Bar-per-day chart matches
 * the mockup's grey axis + orange bars.
 *
 * Data comes from api-sleep-step-tracker-service via `StepTrackerService.getSteps`.
 * The `steps` map on the response is keyed by UTC-midnight ISO strings;
 * we bucket by calendar day-of-month for the bars. Days with no synced
 * data render as empty gaps (no bar, greyed axis tick label — same
 * treatment the mockup shows).
 */

// Axis / chart geometry. `W` is the viewBox width — the SVG is
// responsive via CSS width:100%, so pixel widths here are only a
// coordinate system, not the on-screen size.
const CHART_W = 940;
const CHART_H = 320;
const PAD = { t: 20, r: 20, b: 40, l: 44 };
const IW = CHART_W - PAD.l - PAD.r;
const IH = CHART_H - PAD.t - PAD.b;
const BAR_COLOR = "#ea6b21"; // matches the daily-steps tile accent

const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const monthLabelShort = (year, month0) =>
  `${MONTH_LABELS[month0]} ${year}`;

/** "YYYY-MM" from a JS Date (local time). */
const toYearMonth = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** Parse "YYYY-MM" back to a Date at the first-of-month. */
const fromYearMonth = (ym) => {
  const [y, m] = String(ym || "").split("-").map(Number);
  return new Date(Number.isFinite(y) ? y : 1970, (Number.isFinite(m) ? m : 1) - 1, 1);
};

/** Days-in-month for a 0-indexed month. */
const daysInMonth = (year, month0) =>
  new Date(year, month0 + 1, 0).getDate();

/** First-instant of a month (local) as an ISO with trailing Z. */
const monthStartISO = (year, month0) =>
  new Date(Date.UTC(year, month0, 1, 0, 0, 0, 0)).toISOString();

/** Last-instant of a month (local) as an ISO with trailing Z. */
const monthEndISO = (year, month0) => {
  const d = daysInMonth(year, month0);
  return new Date(Date.UTC(year, month0, d, 23, 59, 59, 999)).toISOString();
};

/** Chart-friendly y-axis top: 10 000 minimum, otherwise the next 2 500 above max. */
const niceMax = (raw) => {
  if (!raw || raw <= 0) return 10000;
  const step = 2500;
  return Math.ceil(raw / step) * step;
};

/** "6,420" — Indian-agnostic thousands grouping. */
const fmtInt = (n) => (n || 0).toLocaleString("en-US");

/** "17 Jun 2026". */
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getUTCDate()).padStart(2, "0")} ${MONTH_LABELS[d.getUTCMonth()].slice(0, 3)} ${d.getUTCFullYear()}`;
};

const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M6 18L18 6" />
  </svg>
);
const CalendarGlyph = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
    stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M3.5 10h17" />
  </svg>
);

const StepTrendModal = ({ patientId, initialISO, onClose }) => {
  const service = useRef(new StepTrackerService()).current;

  // Default to the month of the caller's `initialISO` (e.g. the newest
  // sync date the card resolved). Falls back to the current month when
  // the caller doesn't know or the ISO is unparseable — that keeps the
  // modal usable in a naked-open flow.
  const today = useMemo(() => new Date(), []);
  const [ym, setYm] = useState(() => {
    if (initialISO) {
      const d = new Date(initialISO);
      if (!isNaN(d.getTime())) {
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      }
    }
    return toYearMonth(today);
  });
  const [year, month0] = useMemo(() => {
    const d = fromYearMonth(ym);
    return [d.getFullYear(), d.getMonth()];
  }, [ym]);

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  // Map { dayOfMonth (1..31) → step count }
  const [byDay, setByDay] = useState({});
  // Newest data point in the current month (for the footer summary).
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setByDay({});
    setLatest(null);
    service.getSteps(
      patientId,
      monthStartISO(year, month0),
      monthEndISO(year, month0),
      (res) => {
        if (cancelled) return;
        const m = (res && res.steps) || {};
        const buckets = {};
        let latestISO = "";
        let latestVal = 0;
        Object.keys(m).forEach((iso) => {
          const d = new Date(iso);
          if (isNaN(d.getTime())) return;
          if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month0) return;
          const day = d.getUTCDate();
          buckets[day] = (buckets[day] || 0) + Number(m[iso] || 0);
          if (!latestISO || iso > latestISO) {
            latestISO = iso;
            latestVal = buckets[day];
          }
        });
        setByDay(buckets);
        setLatest(latestISO ? { iso: latestISO, value: latestVal } : null);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setError((err && err.message) || "Could not load step data.");
        setLoading(false);
      },
    );
    return () => { cancelled = true; };
  }, [patientId, year, month0, service]);

  // Chart geometry.
  const dim = daysInMonth(year, month0);
  const maxSteps = niceMax(Math.max(0, ...Object.values(byDay)));
  const barSlot = IW / dim;
  const barWidth = Math.min(24, Math.max(6, barSlot * 0.6));

  // Y-axis ticks at 0 / 25% / 50% / 75% / 100% of the nice max, formatted
  // in "k" units matching the mockup.
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => {
    const v = Math.round(maxSteps * f);
    return { v, label: v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v) };
  });

  // X-axis day labels — show every 2nd day (1, 3, 5…) to avoid crowding on
  // 30/31 day months but keep the dense look the mockup has.
  const xLabels = Array.from({ length: dim }, (_, i) => i + 1)
    .filter((d) => d === 1 || d === dim || d % 2 === 0);

  const goPrev = () => {
    const d = new Date(year, month0 - 1, 1);
    setYm(toYearMonth(d));
  };
  const goNext = () => {
    const d = new Date(year, month0 + 1, 1);
    setYm(toYearMonth(d));
  };
  // Prevent forward navigation past the current month (no data ahead).
  const cannotGoNext = year === today.getFullYear() && month0 === today.getMonth();

  return (
    <div className="st-backdrop" role="presentation" onClick={onClose}>
      <div
        className="st-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Step count trend"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="st-head">
          <h2 className="st-title">Step Count Trend</h2>
          <button type="button" className="st-close" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </header>

        <div className="st-nav">
          <button
            type="button"
            className="st-navbtn"
            onClick={goPrev}
            aria-label="Previous month"
          >
            <ChevronLeft />
          </button>
          <label className="st-month">
            <span className="st-month-icon" aria-hidden="true"><CalendarGlyph /></span>
            <span className="st-month-label">{monthLabelShort(year, month0)}</span>
            {/* Overlay a native month input so the user can jump by year
                without clicking prev/next 12 times. The visible text
                stays styled; the input is transparent above it. */}
            <input
              type="month"
              className="st-month-input"
              value={ym}
              max={toYearMonth(today)}
              onChange={(e) => e.target.value && setYm(e.target.value)}
              aria-label="Pick month"
            />
          </label>
          <button
            type="button"
            className="st-navbtn"
            onClick={goNext}
            disabled={cannotGoNext}
            aria-label="Next month"
          >
            <ChevronRight />
          </button>
        </div>

        <div className="st-body">
          {loading ? (
            <p className="st-note">Loading step data…</p>
          ) : error ? (
            <p className="st-note is-error">{error}</p>
          ) : (
            <svg
              className="st-chart"
              viewBox={`0 0 ${CHART_W} ${CHART_H}`}
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={`Step count for ${monthLabelShort(year, month0)}`}
            >
              {/* Y grid + labels */}
              {yTicks.map((t, i) => {
                const y = PAD.t + IH - (t.v / maxSteps) * IH;
                return (
                  <g key={`ty-${i}`}>
                    <line x1={PAD.l} x2={CHART_W - PAD.r} y1={y} y2={y}
                      stroke="#eef0f4" />
                    <text x={PAD.l - 8} y={y + 3} fontSize="11" fill="#8a94a6" textAnchor="end">
                      {t.label}
                    </text>
                  </g>
                );
              })}
              {/* Bars */}
              {Array.from({ length: dim }, (_, i) => i + 1).map((day) => {
                const v = byDay[day];
                if (!v) return null; // no data → no bar (day label shown greyed)
                const h = Math.max(2, (v / maxSteps) * IH);
                const x = PAD.l + (day - 0.5) * barSlot - barWidth / 2;
                const y = PAD.t + IH - h;
                return (
                  <g key={`bar-${day}`}>
                    <rect
                      x={x} y={y} width={barWidth} height={h}
                      rx="3" ry="3" fill={BAR_COLOR}
                    >
                      <title>{`${day} ${MONTH_LABELS[month0].slice(0, 3)} · ${fmtInt(v)} steps`}</title>
                    </rect>
                  </g>
                );
              })}
              {/* X labels */}
              {xLabels.map((day) => {
                const x = PAD.l + (day - 0.5) * barSlot;
                const has = !!byDay[day];
                return (
                  <text
                    key={`tx-${day}`}
                    x={x} y={PAD.t + IH + 16}
                    fontSize="11"
                    fill={has ? "#0f172a" : "#cbd5e1"}
                    textAnchor="middle"
                  >
                    {day}
                  </text>
                );
              })}
              {/* Month caption under the axis */}
              <text
                x={PAD.l + IW / 2} y={CHART_H - 6}
                fontSize="11" fill="#8a94a6" textAnchor="middle"
              >
                {monthLabelShort(year, month0)}
              </text>
            </svg>
          )}
        </div>

        <footer className="st-foot">
          {!loading && !error && latest ? (
            <p className="st-summary">
              Last updated value: <strong>{fmtInt(latest.value)} steps</strong>
              <span className="st-summary-date">({fmtDate(latest.iso)})</span>
            </p>
          ) : !loading && !error ? (
            <p className="st-summary">No step data recorded this month.</p>
          ) : null}
        </footer>
      </div>
    </div>
  );
};

StepTrendModal.propTypes = {
  patientId:  PropTypes.number.isRequired,
  /** ISO date to seed the initial month view (typically the newest
   *  sync date the caller already resolved). Falls back to current
   *  month when null / unparseable. */
  initialISO: PropTypes.string,
  onClose:    PropTypes.func.isRequired,
};
StepTrendModal.defaultProps = {
  initialISO: null,
};

export default StepTrendModal;
